import React, { useCallback, useEffect, useRef, useState } from 'react';
import Loading from '../../common/Loading/Loading';
import EmptyState from '../../common/EmptyState/EmptyState';
import Button from '../../common/Button/Button';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import FormInput from '../../common/FormInput/FormInput';
import Icon from '../../common/Icon/Icon';
import Pagination from '../../common/Pagination/Pagination';
import StudentNotesModal from '../../common/StudentNotesModal/StudentNotesModal';
import ExpandableFupText from '../../common/ExpandableFupText/ExpandableFupText';
import NotesSummaryCard from '../../common/TextSummaryCard/NotesSummaryCard';
import ViewLinkButton from '../../common/ViewLinkButton/ViewLinkButton';
import ColorDot from '../../common/ColorDot/ColorDot';
import ManagedFupResponseModal, {
  type ManagedFupDetails,
} from '../../../pages/Orchestrator/ManagedFupResponseModal';
import FupCopyLinkButton from '../../../pages/Orchestrator/FupCopyLinkButton';
import {
  getFupLinkStatusLabel,
  resolveFupLinkVisualStatus,
} from '../../../pages/Orchestrator/fupLinkDisplay';
import type { FupLink } from '../../../types/orchestrator';
import { useAuth } from '../../../contexts/AuthContext';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import {
  StudentConsolidatedHistoryService,
  StudentNoteSummaryService,
  StudentService,
  WorkoutAssignmentService,
} from '../../../services';
import StudentNotesHistorySection from '../../common/StudentNotesHistorySection/StudentNotesHistorySection';
import LaunchWorkoutModal from '../../../pages/Performance/components/LaunchWorkoutModal';
import type { PaginationInfo } from '../../../types';
import type {
  PerformanceSupportHistoryItem,
  PerformanceSupportResponseChoice,
} from '../../../types/performanceSupport';
import {
  PERFORMANCE_SUPPORT_REASON_LABELS,
  PERFORMANCE_SUPPORT_RESPONSE_CHOICE_LABELS,
} from '../../../types/performanceSupport';
import type { SensitivePeriodHistoryItem } from '../../../types/sensitiveStudent';
import type {
  StudentConsolidatedHistoryResponse,
  StudentFupHistoryItem,
  StudentMariContactItem,
  StudentWorkoutHistoryItem,
} from '../../../types/studentConsolidatedHistory';
import { formatDate, formatDateTime, splitBrazilContactedAt, validateStudentNote } from '../../../utils';
import { competenceMonthToIsoDate, getBrazilCivilDate, getBrazilCurrentCompetenceMonth } from '../../../utils/competence';
import { canAccessOrchestrator, canEditStudentPersonalInfo, canInsertStudentWorkoutHistory } from '../../../utils/permissions';
import { canWriteStudentObservations } from '../../../utils/studentNotesModalAccess';
import PerformanceSupportSection from '../../../pages/Students/StudentRegister/steps/PerformanceSupportSection';
import SensitiveStudentSection from '../../../pages/Students/StudentRegister/steps/SensitiveStudentSection';
import StudentMariContactChronometer from '../StudentMariContactChronometer/StudentMariContactChronometer';
import StudentMariContactModal from '../StudentMariContactModal/StudentMariContactModal';
import styles from './StudentConsolidatedHistoryPanel.module.css';

const NOTES_PAGE_SIZE = 5;
const HISTORY_SECTIONS = [
  { key: 'sensitive', label: 'Aluna sensível', icon: 'favorite' },
  { key: 'mari-contacts', label: 'Contatos da Mari', icon: 'call' },
  { key: 'fups', label: 'FUPs', icon: 'fact_check' },
  { key: 'notes', label: 'Observações', icon: 'edit_note' },
  { key: 'coaches', label: 'Professoras', icon: 'person' },
  { key: 'performance-support', label: 'Suporte da performance', icon: 'support_agent' },
  { key: 'fup-attempts', label: 'Tentativas de FUP', icon: 'link' },
  { key: 'workouts', label: 'Treinos', icon: 'exercise' },
] as const;
type HistorySectionKey = typeof HISTORY_SECTIONS[number]['key'];
type LocalHistorySection = 'sensitive' | 'performance-support';
type ApiHistorySection = Exclude<HistorySectionKey, LocalHistorySection>;

function performanceSupportResponseLabel(
  choice: PerformanceSupportResponseChoice | null,
): string {
  if (!choice) {
    return '—';
  }

  return PERFORMANCE_SUPPORT_RESPONSE_CHOICE_LABELS[choice];
}
const NOTES_QUERY_SCHEMA = {
  notesPage: queryFilter.integer(1, { min: 1 }),
};

const EMPTY_NOTES_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: NOTES_PAGE_SIZE,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

function emptyHistoryShell(
  studentId: number,
  notesPage: number,
): StudentConsolidatedHistoryResponse {
  return {
    student: { id: studentId, full_name: '', email: null },
    workout_history: [],
    fup_history: [],
    fup_link_attempts: [],
    student_notes: [],
    notes_pagination: {
      ...EMPTY_NOTES_PAGINATION,
      page: notesPage,
    },
    notes_summary: null,
    coach_history: [],
    mari_contacts: [],
    elapsed_since_last_mari_contact: null,
  };
}

function mergeHistorySection(
  current: StudentConsolidatedHistoryResponse | null,
  studentId: number,
  notesPage: number,
  section: ApiHistorySection,
  response: StudentConsolidatedHistoryResponse,
): StudentConsolidatedHistoryResponse {
  const base = current ?? emptyHistoryShell(studentId, notesPage);
  const next: StudentConsolidatedHistoryResponse = {
    ...base,
    student: response.student,
  };

  if (section === 'workouts') {
    next.workout_history = response.workout_history;
  }
  if (section === 'fups') {
    next.fup_history = response.fup_history;
  }
  if (section === 'fup-attempts') {
    next.fup_link_attempts = response.fup_link_attempts;
  }
  if (section === 'notes') {
    next.student_notes = response.student_notes;
    next.notes_pagination = response.notes_pagination;
    next.notes_summary = response.notes_summary;
  }
  if (section === 'coaches') {
    next.coach_history = response.coach_history;
  }
  if (section === 'mari-contacts') {
    next.mari_contacts = response.mari_contacts;
    next.elapsed_since_last_mari_contact = response.elapsed_since_last_mari_contact;
  }

  return next;
}

function toDateOnly(value: string | null | undefined): string {
  const date = value?.slice(0, 10) ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
}

function competenceAfterPrevious(previousCompetence: string | null | undefined): string {
  const match = previousCompetence?.match(/^(\d{4})-(\d{2})/);
  if (!match) return '';
  const nextMonth = new Date(Date.UTC(Number(match[1]), Number(match[2]), 1));
  return `${nextMonth.getUTCFullYear()}-${String(nextMonth.getUTCMonth() + 1).padStart(2, '0')}`;
}

function toManagedFup(
  item: Pick<StudentFupHistoryItem, 'student_fup_id' | 'workout_assignment_id' | 'created_at'> & {
    responded_at?: string | null;
  },
  studentId: number,
  studentName: string,
): ManagedFupDetails {
  return {
    studentId,
    studentFupId: item.student_fup_id ?? 0,
    assignmentId: item.workout_assignment_id,
    studentName,
    createdAt: item.responded_at ?? item.created_at,
  };
}

interface StudentConsolidatedHistoryPanelProps {
  studentId: number;
}

function toFupLinkForDisplay(
  item: Pick<StudentConsolidatedHistoryResponse['fup_link_attempts'][number], 'attempt_number' | 'created_at' | 'responded_at'>,
): FupLink {
  return {
    token: `${item.attempt_number}-${item.created_at}`,
    attempt_number: item.attempt_number,
    created_at: item.created_at,
    responded_at: item.responded_at,
  };
}

const StudentConsolidatedHistoryPanel: React.FC<StudentConsolidatedHistoryPanelProps> = ({
  studentId,
}) => {
  const { roles } = useAuth();
  const { filters, setFilter } = useQueryFilters(NOTES_QUERY_SCHEMA, { pageKey: 'notesPage' });
  const [history, setHistory] = useState<StudentConsolidatedHistoryResponse | null>(null);
  const [sensitiveHistory, setSensitiveHistory] = useState<SensitivePeriodHistoryItem[]>([]);
  const [performanceSupportHistory, setPerformanceSupportHistory] = useState<PerformanceSupportHistoryItem[]>([]);
  const [editingPregnancy, setEditingPregnancy] = useState<PerformanceSupportHistoryItem | null>(null);
  const [editingPregnantFrom, setEditingPregnantFrom] = useState('');
  const [editingPregnantFromError, setEditingPregnantFromError] = useState<string | null>(null);
  const [savingPregnancy, setSavingPregnancy] = useState(false);
  const [loadingSection, setLoadingSection] = useState<HistorySectionKey | null>(null);
  const [notesRefreshing, setNotesRefreshing] = useState(false);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [confirmNotesSummaryOpen, setConfirmNotesSummaryOpen] = useState(false);
  const [generatingNotesSummary, setGeneratingNotesSummary] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [submittingNotes, setSubmittingNotes] = useState(false);
  const [launchWorkoutModalOpen, setLaunchWorkoutModalOpen] = useState(false);
  const [editingWorkoutHistory, setEditingWorkoutHistory] = useState<StudentWorkoutHistoryItem | null>(null);
  const [pendingWorkoutHistoryDelete, setPendingWorkoutHistoryDelete] = useState<StudentWorkoutHistoryItem | null>(null);
  const [deletingWorkoutHistory, setDeletingWorkoutHistory] = useState(false);
  const [mariContactModalOpen, setMariContactModalOpen] = useState(false);
  const [editingMariContact, setEditingMariContact] = useState<StudentMariContactItem | null>(null);
  const [submittingMariContact, setSubmittingMariContact] = useState(false);
  const [pendingMariContactDelete, setPendingMariContactDelete] = useState<StudentMariContactItem | null>(null);
  const [deletingMariContact, setDeletingMariContact] = useState(false);
  const [pendingFupView, setPendingFupView] = useState<ManagedFupDetails | null>(null);
  const [activeSection, setActiveSection] = useState<HistorySectionKey | null>(null);
  const canRegisterWorkoutHistory = canInsertStudentWorkoutHistory(roles);
  const canWriteObservations = canWriteStudentObservations(roles);
  const canManageMariContacts = canRegisterWorkoutHistory;
  const canManageFup = canAccessOrchestrator(roles);
  const canManageSensitive = canEditStudentPersonalInfo(roles);
  const canManagePerformanceSupport = canEditStudentPersonalInfo(roles);
  const loadedSectionsRef = useRef<Set<HistorySectionKey>>(new Set());
  const loadedStudentIdRef = useRef<number | null>(null);
  const activeSectionRef = useRef<HistorySectionKey | null>(null);
  activeSectionRef.current = activeSection;

  const loadSensitiveHistory = useCallback(async () => {
    const sensitive = await StudentService.getSensitivePeriod(studentId);
    setSensitiveHistory(sensitive.history);
  }, [studentId]);

  const loadPerformanceSupportHistory = useCallback(async () => {
    const support = await StudentService.getPerformanceSupport(studentId);
    const rows = Array.isArray(support.history) ? support.history : [];
    setPerformanceSupportHistory(rows.map((item) => ({
      ...item,
      pregnancyPeriodId: item.pregnancyPeriodId ?? null,
      pregnantFrom: item.pregnantFrom ?? null,
      pregnantUntil: item.pregnantUntil ?? null,
    })));
  }, [studentId]);

  const handleOpenEditPregnancy = useCallback((item: PerformanceSupportHistoryItem) => {
    if (!item.pregnancyPeriodId || !item.pregnantFrom) {
      return;
    }

    setEditingPregnancy(item);
    setEditingPregnantFrom(item.pregnantFrom);
    setEditingPregnantFromError(null);
  }, []);

  const handleSavePregnancy = useCallback(async () => {
    if (!editingPregnancy?.pregnancyPeriodId) {
      return;
    }

    const trimmed = editingPregnantFrom.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      setEditingPregnantFromError('Informe a data em que a aluna ficou grávida.');
      return;
    }

    if (trimmed > getBrazilCivilDate()) {
      setEditingPregnantFromError('A data em que ficou grávida não pode ser no futuro.');
      return;
    }

    setSavingPregnancy(true);
    setEditingPregnantFromError(null);
    try {
      await StudentService.updatePregnancyPeriod(
        studentId,
        editingPregnancy.pregnancyPeriodId,
        trimmed,
      );
      setEditingPregnancy(null);
      setEditingPregnantFrom('');
      await loadPerformanceSupportHistory();
    } catch (error) {
      setEditingPregnantFromError(
        error instanceof Error ? error.message : 'Não foi possível atualizar a data da gravidez.',
      );
    } finally {
      setSavingPregnancy(false);
    }
  }, [editingPregnancy, editingPregnantFrom, loadPerformanceSupportHistory, studentId]);

  const loadSection = useCallback(async (
    sectionKey: HistorySectionKey,
    options: { force?: boolean } = {},
  ): Promise<StudentConsolidatedHistoryResponse | null> => {
    const force = options.force === true;
    if (!force && loadedSectionsRef.current.has(sectionKey)) {
      return null;
    }

    const quietNotesRefresh = sectionKey === 'notes' && loadedSectionsRef.current.has('notes');
    if (quietNotesRefresh) {
      setNotesRefreshing(true);
    } else {
      setLoadingSection(sectionKey);
    }
    setSectionError(null);

    try {
      if (sectionKey === 'sensitive') {
        await loadSensitiveHistory();
        loadedSectionsRef.current.add(sectionKey);
        return null;
      }

      if (sectionKey === 'performance-support') {
        await loadPerformanceSupportHistory();
        loadedSectionsRef.current.add(sectionKey);
        return null;
      }

      const response = await StudentConsolidatedHistoryService.getByStudentId(studentId, {
        section: sectionKey,
        notesPage: filters.notesPage,
        notesPageSize: NOTES_PAGE_SIZE,
      });

      setHistory((current) => mergeHistorySection(
        current,
        studentId,
        filters.notesPage,
        sectionKey,
        response,
      ));
      loadedSectionsRef.current.add(sectionKey);

      if (sectionKey === 'notes' && response.notes_pagination.page !== filters.notesPage) {
        setFilter('notesPage', response.notes_pagination.page, { resetPage: false });
      }

      return response;
    } catch (loadError) {
      if (sectionKey === 'sensitive') {
        setSensitiveHistory([]);
      }
      if (sectionKey === 'performance-support') {
        setPerformanceSupportHistory([]);
      }
      setSectionError(loadError instanceof Error ? loadError.message : 'Erro ao carregar histórico.');
      return null;
    } finally {
      setLoadingSection((current) => (current === sectionKey ? null : current));
      setNotesRefreshing(false);
    }
  }, [filters.notesPage, loadPerformanceSupportHistory, loadSensitiveHistory, setFilter, studentId]);

  const loadHistory = useCallback(async (
    sectionKey?: HistorySectionKey,
  ): Promise<StudentConsolidatedHistoryResponse | null> => {
    const target = sectionKey ?? activeSectionRef.current;
    if (!target) {
      return null;
    }

    return loadSection(target, { force: true });
  }, [loadSection]);

  useEffect(() => {
    if (loadedStudentIdRef.current === studentId) {
      return;
    }

    loadedStudentIdRef.current = studentId;
    loadedSectionsRef.current = new Set();
    setHistory(null);
    setSensitiveHistory([]);
    setPerformanceSupportHistory([]);
    setActiveSection(null);
    setSectionError(null);
    setLoadingSection(null);
  }, [studentId]);

  const toggleSection = useCallback((sectionKey: HistorySectionKey) => {
    setActiveSection((current) => (current === sectionKey ? null : sectionKey));
  }, []);

  useEffect(() => {
    if (!activeSection) {
      return;
    }

    const force = activeSection === 'notes';
    void loadSection(activeSection, { force });
  }, [activeSection, filters.notesPage, loadSection]);

  const handleOpenNotesModal = useCallback(() => {
    setNewNote('');
    setNotesModalOpen(true);
  }, []);

  const handleGenerateNotesSummary = useCallback(async () => {
    if (!canWriteObservations || generatingNotesSummary) {
      return;
    }

    setGeneratingNotesSummary(true);
    try {
      const summary = await StudentNoteSummaryService.generate(studentId);
      setHistory((current) => (current ? { ...current, notes_summary: summary } : current));
      setConfirmNotesSummaryOpen(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível gerar o resumo das observações.');
    } finally {
      setGeneratingNotesSummary(false);
    }
  }, [canWriteObservations, generatingNotesSummary, studentId]);

  const handleNotesPageChange = useCallback((page: number) => {
    setFilter('notesPage', page, { resetPage: false });
  }, [setFilter]);

  const handleDeleteNote = useCallback(async (noteId: number) => {
    await WorkoutAssignmentService.deleteNote(noteId);
    const response = await loadHistory();
    if (
      response
      && response.student_notes.length === 0
      && response.notes_pagination.page > 1
    ) {
      setFilter('notesPage', response.notes_pagination.page - 1, { resetPage: false });
    }
  }, [loadHistory, setFilter]);

  const handleNotesSubmit = useCallback(async () => {
    const trimmedNote = newNote.trim();
    const noteError = validateStudentNote(trimmedNote);
    if (noteError) {
      alert(noteError);
      return;
    }

    setSubmittingNotes(true);
    try {
      await WorkoutAssignmentService.saveNotes({
        studentId,
        note: trimmedNote,
      });
      setNewNote('');
      setNotesModalOpen(false);
      if (filters.notesPage !== 1) {
        setFilter('notesPage', 1, { resetPage: false });
        return;
      }
      await loadHistory();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível salvar as observações.');
    } finally {
      setSubmittingNotes(false);
    }
  }, [filters.notesPage, loadHistory, newNote, setFilter, studentId]);

  const handleOpenCreateWorkoutHistory = useCallback(() => {
    setEditingWorkoutHistory(null);
    setLaunchWorkoutModalOpen(true);
  }, []);

  const handleLaunchWorkoutSaved = useCallback(() => {
    setLaunchWorkoutModalOpen(false);
    void loadHistory().catch((loadError) => {
      alert(loadError instanceof Error ? loadError.message : 'Não foi possível atualizar o histórico da aluna.');
    });
  }, [loadHistory]);

  const handleOpenEditWorkoutHistory = useCallback((item: StudentWorkoutHistoryItem) => {
    setEditingWorkoutHistory(item);
    setLaunchWorkoutModalOpen(true);
  }, []);

  const handleWorkoutHistoryDeleteConfirm = useCallback(async () => {
    if (!pendingWorkoutHistoryDelete) {
      return;
    }

    setDeletingWorkoutHistory(true);
    try {
      const assignmentId = pendingWorkoutHistoryDelete.workout_assignment_id
        ?? pendingWorkoutHistoryDelete.id;
      if (!Number.isInteger(assignmentId) || assignmentId <= 0) {
        alert('Não foi possível identificar o treino para excluir.');
        return;
      }

      await WorkoutAssignmentService.deleteWorkoutAssignment({
        assignmentId,
        studentId,
      });
      setPendingWorkoutHistoryDelete(null);
      await loadHistory();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível excluir o treino.');
    } finally {
      setDeletingWorkoutHistory(false);
    }
  }, [loadHistory, pendingWorkoutHistoryDelete, studentId]);

  const handleOpenCreateMariContact = useCallback(() => {
    setEditingMariContact(null);
    setMariContactModalOpen(true);
  }, []);

  const handleOpenEditMariContact = useCallback((item: StudentMariContactItem) => {
    setEditingMariContact(item);
    setMariContactModalOpen(true);
  }, []);

  const handleCloseMariContactModal = useCallback(() => {
    if (submittingMariContact) {
      return;
    }
    setMariContactModalOpen(false);
    setEditingMariContact(null);
  }, [submittingMariContact]);

  const handleMariContactSubmit = useCallback(async (values: { contactedAt: string }) => {
    setSubmittingMariContact(true);
    try {
      if (editingMariContact) {
        await WorkoutAssignmentService.updateMariContact({
          contactId: editingMariContact.id,
          studentId,
          contactedAt: values.contactedAt,
        });
      } else {
        await WorkoutAssignmentService.insertMariContact({
          studentId,
          contactedAt: values.contactedAt,
        });
      }
      setMariContactModalOpen(false);
      setEditingMariContact(null);
      await loadHistory();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível salvar o contato da Mari.');
    } finally {
      setSubmittingMariContact(false);
    }
  }, [editingMariContact, loadHistory, studentId]);

  const handleMariContactDeleteConfirm = useCallback(async () => {
    if (!pendingMariContactDelete) {
      return;
    }

    setDeletingMariContact(true);
    try {
      await WorkoutAssignmentService.deleteMariContact({
        contactId: pendingMariContactDelete.id,
        studentId,
      });
      setPendingMariContactDelete(null);
      await loadHistory();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível excluir o contato da Mari.');
    } finally {
      setDeletingMariContact(false);
    }
  }, [loadHistory, pendingMariContactDelete, studentId]);

  const workoutHistory = history?.workout_history ?? [];
  const fupHistory = history?.fup_history ?? [];
  const fupLinkAttempts = history?.fup_link_attempts ?? [];
  const studentNotes = history?.student_notes ?? [];
  const notesPagination = history?.notes_pagination ?? {
    ...EMPTY_NOTES_PAGINATION,
    page: filters.notesPage,
  };
  const coachHistory = history?.coach_history ?? [];
  const mariContacts = history?.mari_contacts ?? [];
  const lastMariContactedAt = mariContacts[0]?.contacted_at ?? null;
  const studentName = history?.student.full_name ?? '';
  const notesPager = notesPagination.totalPages > 1 ? (
    <div className={styles.notesPagination}>
      <Pagination
        pagination={notesPagination}
        loading={notesRefreshing}
        onPageChange={handleNotesPageChange}
      />
    </div>
  ) : null;

  const sectionAction = (sectionKey: HistorySectionKey): React.ReactNode => {
    if (sectionKey === 'notes' && canWriteObservations) {
      return (
        <>
          <Button
            type="button"
            variant="outline"
            size="small"
            icon="auto_awesome"
            className={generatingNotesSummary ? styles['generate-summary-busy'] : undefined}
            disabled={generatingNotesSummary}
            aria-busy={generatingNotesSummary}
            onClick={() => setConfirmNotesSummaryOpen(true)}
          >
            Gerar resumo
          </Button>
          <ViewLinkButton
            label="Registrar"
            icon="add_notes"
            onClick={handleOpenNotesModal}
          />
        </>
      );
    }
    if (sectionKey === 'mari-contacts' && canManageMariContacts) {
      return (
        <ViewLinkButton
          label="Informar contato"
          icon="add_notes"
          onClick={handleOpenCreateMariContact}
        />
      );
    }
    if (sectionKey === 'workouts' && canRegisterWorkoutHistory) {
      return (
        <ViewLinkButton
          label="Registrar"
          icon="add_notes"
          onClick={handleOpenCreateWorkoutHistory}
        />
      );
    }
    if (sectionKey === 'fup-attempts' && canManageFup) {
      return (
        <FupCopyLinkButton
          studentId={studentId}
          assignmentId={null}
          competence={competenceMonthToIsoDate(getBrazilCurrentCompetenceMonth())}
          onLinkCopied={() => {
            void loadHistory('fup-attempts');
          }}
        />
      );
    }
    return null;
  };

  const renderSectionContent = (sectionKey: HistorySectionKey): React.ReactNode => {
    if (loadingSection === sectionKey) {
      return <Loading />;
    }

    if (activeSection === sectionKey && sectionError) {
      return <EmptyState message={`Erro ao carregar histórico: ${sectionError}`} />;
    }

    if (sectionKey === 'coaches') {
      if (coachHistory.length === 0) {
        return <p className={styles.empty}>Nenhuma troca de professora registrada</p>;
      }
      return (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Professora</th>
              <th>Professora anterior</th>
              <th>Início</th>
              <th>Fim</th>
              <th>Alterado por</th>
            </tr>
          </thead>
          <tbody>
            {coachHistory.map((item) => (
              <tr key={item.id}>
                <td>{item.coach_name ?? <span className={styles['empty-value']}>—</span>}</td>
                <td>{item.previous_coach_name ?? <span className={styles['empty-value']}>—</span>}</td>
                <td>{formatDateTime(item.assigned_at)}</td>
                <td>
                  {item.unassigned_at
                    ? formatDateTime(item.unassigned_at)
                    : 'Atual'}
                </td>
                <td>{item.changed_by_name ?? <span className={styles['empty-value']}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (sectionKey === 'notes') {
      return (
        <>
          <NotesSummaryCard value={history?.notes_summary} />
          <StudentNotesHistorySection
            notes={studentNotes}
            title=""
            disableScroll
            compact
            footer={notesPager}
            onDeleteNote={canWriteObservations ? handleDeleteNote : undefined}
          />
        </>
      );
    }

    if (sectionKey === 'mari-contacts') {
      return (
        <>
          <StudentMariContactChronometer contactedAt={lastMariContactedAt} />
          {mariContacts.length === 0 ? (
            <p className={styles.empty}>Nenhum contato registrado</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Data e hora</th>
                  <th>Registrado por</th>
                  {canManageMariContacts && <th>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {mariContacts.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDateTime(item.contacted_at)}</td>
                    <td>{item.created_by_name ?? <span className={styles['empty-value']}>—</span>}</td>
                    {canManageMariContacts && (
                      <td>
                        <div className={styles['row-actions']}>
                          <button
                            type="button"
                            className={styles['icon-button']}
                            aria-label="Editar contato da Mari"
                            title="Editar contato da Mari"
                            onClick={() => handleOpenEditMariContact(item)}
                            disabled={deletingMariContact}
                          >
                            <Icon name="edit" size={18} color="var(--color-brand-primary)" />
                          </button>
                          <button
                            type="button"
                            className={styles['icon-button']}
                            aria-label="Excluir contato da Mari"
                            title="Excluir contato da Mari"
                            onClick={() => setPendingMariContactDelete(item)}
                            disabled={deletingMariContact}
                          >
                            <Icon name="delete" size={18} color="var(--color-solid-danger)" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      );
    }

    if (sectionKey === 'sensitive') {
      return (
        <div className={styles['sensitive-section']}>
          <SensitiveStudentSection
            studentId={studentId}
            readOnly={!canManageSensitive}
            embedded
            onChanged={() => {
              void loadSensitiveHistory();
            }}
          />
          {sensitiveHistory.length === 0 ? (
            <p className={styles.empty}>Nenhum período sensível no histórico</p>
          ) : (
            <>
              <h4 className={styles['sensitive-history-title']}>Histórico</h4>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Início</th>
                    <th>Encerrado em</th>
                    <th>Motivo</th>
                    <th>Justificativa</th>
                  </tr>
                </thead>
                <tbody>
                  {sensitiveHistory.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(getBrazilCivilDate(new Date(item.startedAt)))}</td>
                      <td>
                        {item.endedAt
                          ? formatDate(getBrazilCivilDate(new Date(item.endedAt)))
                          : 'Atual'}
                      </td>
                      <td>{item.reason}</td>
                      <td>
                        {item.endJustification
                          ?? <span className={styles['empty-value']}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      );
    }

    if (sectionKey === 'performance-support') {
      return (
        <div className={styles['sensitive-section']}>
          <PerformanceSupportSection
            studentId={studentId}
            readOnly={!canManagePerformanceSupport}
            embedded
            onChanged={() => {
              void loadPerformanceSupportHistory();
            }}
          />
          {performanceSupportHistory.length === 0 ? (
            <p className={styles.empty}>Nenhum suporte da performance no histórico</p>
          ) : (
            <>
              <h4 className={styles['sensitive-history-title']}>Histórico</h4>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Registrado em</th>
                    <th>Suporte</th>
                    <th>Ficou grávida em</th>
                    <th>Tag até</th>
                    <th>Respondido</th>
                    <th>Respondido em</th>
                    {canManagePerformanceSupport ? <th>Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {performanceSupportHistory.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(getBrazilCivilDate(new Date(item.createdAt)))}</td>
                      <td>{PERFORMANCE_SUPPORT_REASON_LABELS[item.reason]}</td>
                      <td>
                        {item.pregnantFrom
                          ? formatDate(item.pregnantFrom)
                          : <span className={styles['empty-value']}>—</span>}
                      </td>
                      <td>
                        {item.pregnantUntil
                          ? formatDate(item.pregnantUntil)
                          : <span className={styles['empty-value']}>—</span>}
                      </td>
                      <td>
                        {item.responseChoice
                          ? performanceSupportResponseLabel(item.responseChoice)
                          : <span className={styles['empty-value']}>—</span>}
                      </td>
                      <td>
                        {item.respondedAt
                          ? formatDateTime(item.respondedAt)
                          : <span className={styles['empty-value']}>—</span>}
                      </td>
                      {canManagePerformanceSupport ? (
                        <td>
                          {item.reason === 'became_pregnant' && item.pregnancyPeriodId ? (
                            <ViewLinkButton
                              label="Editar"
                              icon="edit"
                              onClick={() => handleOpenEditPregnancy(item)}
                            />
                          ) : (
                            <span className={styles['empty-value']}>—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      );
    }

    if (sectionKey === 'workouts') {
      if (workoutHistory.length === 0) {
        return <p className={styles.empty}>Nenhum treino registrado</p>;
      }
      return (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Código do treino</th>
              <th>Data prevista de entrega</th>
              <th>Data entrega</th>
              <th>Entregue por</th>
              <th>Status</th>
              {canRegisterWorkoutHistory && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {workoutHistory.map((item) => (
              <tr key={item.id}>
                <td>{item.workout_code ?? <span className={styles['empty-value']}>—</span>}</td>
                <td>
                  {item.expected_delivery_date
                    ? formatDate(item.expected_delivery_date)
                    : <span className={styles['empty-value']}>—</span>}
                </td>
                <td>
                  {item.delivered_at
                    ? formatDateTime(item.delivered_at)
                    : <span className={styles['empty-value']}>—</span>}
                </td>
                <td>{item.delivered_by_name ?? <span className={styles['empty-value']}>—</span>}</td>
                <td>{item.assignment_status_label ?? <span className={styles['empty-value']}>—</span>}</td>
                {canRegisterWorkoutHistory && (
                  <td>
                    <div className={styles['row-actions']}>
                      <button
                        type="button"
                        className={styles['icon-button']}
                        aria-label="Editar histórico de treino"
                        title="Editar histórico de treino"
                        onClick={() => handleOpenEditWorkoutHistory(item)}
                        disabled={deletingWorkoutHistory}
                      >
                        <Icon name="edit" size={18} color="var(--color-brand-primary)" />
                      </button>
                      {item.can_delete ? (
                        <button
                          type="button"
                          className={styles['icon-button']}
                          aria-label="Excluir treino lançado"
                          title="Excluir treino lançado"
                          onClick={() => setPendingWorkoutHistoryDelete(item)}
                          disabled={deletingWorkoutHistory}
                        >
                          <Icon name="delete" size={18} color="var(--color-solid-danger)" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (sectionKey === 'fups') {
      if (fupHistory.length === 0) {
        return <p className={styles.empty}>Nenhum FUP registrado</p>;
      }
      return (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Competência</th>
              <th>Treino</th>
              <th>Resposta</th>
            </tr>
          </thead>
          <tbody>
            {fupHistory.map((item, index) => (
              <tr key={`${item.competency}-${item.created_at ?? index}`}>
                <td>{item.competency_label}</td>
                <td>{item.workout_code ?? <span className={styles['empty-value']}>—</span>}</td>
                <td>
                  {item.response ? (
                    <div className={styles['fup-response-cell']}>
                      <ExpandableFupText
                        text={item.response}
                        contentKey={`${item.competency}-${item.created_at ?? index}`}
                      />
                      <ViewLinkButton
                        label="Ver resposta"
                        icon={null}
                        onClick={() => setPendingFupView(toManagedFup(item, studentId, studentName))}
                      />
                    </div>
                  ) : (
                    <span className={styles['empty-value']}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (fupLinkAttempts.length === 0) {
      return <p className={styles.empty}>Nenhuma tentativa de FUP</p>;
    }

    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Tentativa</th>
            <th>Criado em</th>
            <th>Respondido em</th>
            <th>Status</th>
            <th>Resposta</th>
          </tr>
        </thead>
        <tbody>
          {fupLinkAttempts.map((item) => {
            const visualStatus = resolveFupLinkVisualStatus(toFupLinkForDisplay(item));
            const statusLabel = getFupLinkStatusLabel(visualStatus);

            return (
              <tr key={`${item.attempt_number}-${item.created_at}`}>
                <td>{item.attempt_number}</td>
                <td>{formatDateTime(item.created_at)}</td>
                <td>
                  {item.responded_at
                    ? formatDateTime(item.responded_at)
                    : <span className={styles['empty-value']}>—</span>}
                </td>
                <td>
                  <span className={styles['status-cell']}>
                    <ColorDot
                      variant={visualStatus}
                      aria-label={statusLabel}
                      title={statusLabel}
                    />
                    {statusLabel}
                  </span>
                </td>
                <td>
                  {item.response ? (
                    <ViewLinkButton
                      label="Ver resposta"
                      icon={null}
                      onClick={() => setPendingFupView(toManagedFup(item, studentId, studentName))}
                    />
                  ) : (
                    <span className={styles['empty-value']}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.accordion}>
        {HISTORY_SECTIONS.map((section) => {
          const isOpen = activeSection === section.key;
          const action = sectionAction(section.key);
          const headerClassName = [
            styles['accordion-header'],
            isOpen ? styles['accordion-header-open'] : '',
          ].filter(Boolean).join(' ');

          return (
            <section key={section.key} className={styles['accordion-item']}>
              <div className={headerClassName}>
                <button
                  type="button"
                  className={styles['accordion-toggle']}
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.key)}
                >
                  <span className={styles['accordion-lead']}>
                    <Icon
                      name={section.icon}
                      size={20}
                      color={isOpen ? 'var(--color-accent-brown)' : 'var(--color-text-muted)'}
                    />
                    <span className={styles['accordion-title']}>{section.label}</span>
                  </span>
                  <Icon
                    name={isOpen ? 'expand_less' : 'expand_more'}
                    size={22}
                    color="var(--color-text-muted)"
                  />
                </button>
              </div>
              <div
                className={[
                  styles['accordion-body'],
                  isOpen ? styles['accordion-body-open'] : '',
                ].filter(Boolean).join(' ')}
                aria-hidden={!isOpen}
              >
                <div className={styles['accordion-body-inner']}>
                  <div className={styles['accordion-body-content']}>
                    {action ? (
                      <div className={styles['section-actions']}>
                        {action}
                      </div>
                    ) : null}
                    {renderSectionContent(section.key)}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <StudentNotesModal
        open={notesModalOpen}
        studentId={studentId}
        studentName={studentName}
        notes={studentNotes}
        historyFooter={notesPager}
        summary={history?.notes_summary}
        value={newNote}
        loading={submittingNotes}
        canWriteObservations={canWriteObservations}
        onChange={setNewNote}
        onSummaryGenerated={(summary) => {
          setHistory((current) => current ? { ...current, notes_summary: summary } : current);
        }}
        onCancel={() => setNotesModalOpen(false)}
        onSubmit={handleNotesSubmit}
        onDeleteNote={handleDeleteNote}
      />

      <ConfirmModal
        open={confirmNotesSummaryOpen}
        variant="warning"
        icon="auto_awesome"
        title="Gerar resumo?"
        message="Deseja gerar o resumo das observações desta aluna? O resumo atual será substituído."
        confirmLabel="Sim"
        cancelLabel="Cancelar"
        confirmIcon="auto_awesome"
        loading={generatingNotesSummary}
        loadingLabel="Gerando..."
        onCancel={() => {
          if (!generatingNotesSummary) {
            setConfirmNotesSummaryOpen(false);
          }
        }}
        onConfirm={() => {
          void handleGenerateNotesSummary();
        }}
      />

      <ConfirmModal
        open={editingPregnancy !== null}
        title="Editar gravidez"
        variant="warning"
        alignment="left"
        width={520}
        loading={savingPregnancy}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        onCancel={() => {
          if (!savingPregnancy) {
            setEditingPregnancy(null);
            setEditingPregnantFrom('');
            setEditingPregnantFromError(null);
          }
        }}
        onConfirm={() => {
          void handleSavePregnancy();
        }}
      >
        <div className={styles['pregnancy-edit-body']}>
          <FormInput
            label="Data em que ficou grávida"
            type="date"
            value={editingPregnantFrom}
            max={getBrazilCivilDate()}
            required
            error={editingPregnantFromError ?? undefined}
            onChange={(event) => {
              setEditingPregnantFrom(event.target.value);
              setEditingPregnantFromError(null);
            }}
          />
          <p className={styles['pregnancy-edit-hint']}>
            A tag “Grávida” acompanha a aluna por 40 semanas a partir dessa data.
          </p>
        </div>
      </ConfirmModal>

      <ManagedFupResponseModal
        fup={pendingFupView}
        canManage={canManageFup}
        onClose={() => setPendingFupView(null)}
        onSaved={(result) => {
          setPendingFupView((current) => (
            current
              ? {
                ...current,
                response: result.response,
                answers: result.answers,
              }
              : current
          ));
          void loadHistory();
        }}
        onDeleted={() => {
          void loadHistory();
        }}
      />

      <LaunchWorkoutModal
        open={launchWorkoutModalOpen}
        initialStudentId={studentId}
        initialStudentName={studentName}
        lockExpectedDeliveryDate={Boolean(editingWorkoutHistory)}
        historyEdit={editingWorkoutHistory?.workout_history_id ? {
          historyId: editingWorkoutHistory.workout_history_id,
          assignmentId: editingWorkoutHistory.workout_assignment_id,
          competence: competenceAfterPrevious(editingWorkoutHistory.assignment_competence)
            || toDateOnly(editingWorkoutHistory.last_workout_date).slice(0, 7),
          previousTrainingCode: editingWorkoutHistory.previous_training_code ?? '',
          expectedDeliveryDate: toDateOnly(editingWorkoutHistory.expected_delivery_date)
            || toDateOnly(editingWorkoutHistory.delivered_at),
          status: editingWorkoutHistory.assignment_status === 'delivered' ? 'delivered' : 'scheduled',
          lastWorkoutDate: toDateOnly(editingWorkoutHistory.last_workout_date),
          workoutCode: editingWorkoutHistory.workout_code ?? '',
          deliveredAt: editingWorkoutHistory.delivered_at ?? '',
          deliveredByPortalUserId: editingWorkoutHistory.delivered_by_portal_user_id ?? 0,
        } : undefined}
        cycleEdit={editingWorkoutHistory && !editingWorkoutHistory.workout_history_id ? {
          assignmentId: editingWorkoutHistory.id,
          previousTrainingCode: editingWorkoutHistory.previous_training_code ?? '',
          nextTrainingCode: editingWorkoutHistory.next_training_code
            ?? editingWorkoutHistory.workout_code
            ?? '',
          expectedDeliveryDate: toDateOnly(editingWorkoutHistory.expected_delivery_date),
          status: editingWorkoutHistory.assignment_status === 'delivered' ? 'delivered' : 'scheduled',
          deliveredAt: editingWorkoutHistory.delivered_at ?? undefined,
        } : undefined}
        onClose={() => {
          setLaunchWorkoutModalOpen(false);
          setEditingWorkoutHistory(null);
        }}
        onSaved={() => {
          setEditingWorkoutHistory(null);
          handleLaunchWorkoutSaved();
        }}
      />

      <ConfirmModal
        open={Boolean(pendingWorkoutHistoryDelete)}
        title="Excluir treino lançado"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deletingWorkoutHistory}
        onCancel={() => {
          if (!deletingWorkoutHistory) {
            setPendingWorkoutHistoryDelete(null);
          }
        }}
        onConfirm={handleWorkoutHistoryDeleteConfirm}
      >
        <p className={styles.confirmMessage}>
          Excluir este treino lançado manualmente? O ciclo e o histórico ligados serão removidos.
        </p>
      </ConfirmModal>

      <StudentMariContactModal
        open={mariContactModalOpen}
        formKey={editingMariContact ? `edit-${editingMariContact.id}` : 'create'}
        title={editingMariContact ? 'Editar contato da Mari' : 'Informar contato da Mari'}
        initialValues={(() => {
          if (!editingMariContact) {
            return null;
          }
          const parts = splitBrazilContactedAt(editingMariContact.contacted_at);
          return parts
            ? { contactDate: parts.date, contactTime: parts.time }
            : null;
        })()}
        loading={submittingMariContact}
        onCancel={handleCloseMariContactModal}
        onSubmit={handleMariContactSubmit}
      />

      <ConfirmModal
        open={Boolean(pendingMariContactDelete)}
        title="Excluir contato da Mari"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deletingMariContact}
        onCancel={() => {
          if (!deletingMariContact) {
            setPendingMariContactDelete(null);
          }
        }}
        onConfirm={handleMariContactDeleteConfirm}
      >
        <p className={styles.confirmMessage}>
          Excluir este contato da Mari?
        </p>
      </ConfirmModal>
    </div>
  );
};

export default StudentConsolidatedHistoryPanel;
